import { useCallback, useState } from 'react';

import {
  operationalBriefingHTMLTemplate,
  workEthicsHTMLTemplate,
} from '@pharmaplan/common';
import {
  getAiOnboardingOperationalBriefingDocument,
  getAiOnboardingWorkEthicsDocument,
} from '@pharmaplan/common/actions/aiOnboardingActions';

import strings from '../../../localization';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

type PolicyType = 'operationalBriefing' | 'workEthics';

interface IUseManualSignupPolicyDialogs {
  onWorkEthicsAccepted: () => void;
}

const useManualSignupPolicyDialogs = ({
  onWorkEthicsAccepted,
}: IUseManualSignupPolicyDialogs) => {
  const dispatch = useAppDispatch();
  const [activePolicy, setActivePolicy] = useState<PolicyType | null>(null);
  const operationalBriefingHTML = useAppSelector(
    operationalBriefingHTMLTemplate,
  );
  const workEthicsHTML = useAppSelector(workEthicsHTMLTemplate);

  const openPolicy = useCallback((policy: PolicyType) => {
    setActivePolicy(policy);

    dispatch(
      policy === 'operationalBriefing'
        ? getAiOnboardingOperationalBriefingDocument()
        : getAiOnboardingWorkEthicsDocument(),
    );
  }, [dispatch]);

  const closePolicy = useCallback(() => {
    setActivePolicy(null);
  }, []);

  const acceptWorkEthics = useCallback(() => {
    onWorkEthicsAccepted();
    closePolicy();
  }, [closePolicy, onWorkEthicsAccepted]);

  return {
    openOperationalBriefing: () =>
      openPolicy('operationalBriefing'),
    openWorkEthics: () =>
      openPolicy('workEthics'),
    operationalBriefingDialogProps: {
      content: operationalBriefingHTML,
      isOpen: activePolicy === 'operationalBriefing',
      onClose: closePolicy,
      primaryActionLabel: strings.close,
      title: strings.operationalBriefing,
    },
    workEthicsDialogProps: {
      content: workEthicsHTML,
      isOpen: activePolicy === 'workEthics',
      onAccept: acceptWorkEthics,
      primaryActionLabel: strings.accept,
      title: strings.workEthics,
    },
  };
};

export default useManualSignupPolicyDialogs;
